import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Users } from "lucide-react";

export default function AvailabilityPage() {
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [bookings, setBookings] = useState([]);
    const [staff, setStaff] = useState([]);

    const load = async () => {
        try {
            const [avail, s] = await Promise.all([
                api.get("/staff/availability", { params: { date } }),
                api.get("/staff"),
            ]);
            setBookings(avail.data.bookings || []);
            setStaff(s.data);
        } catch {
            toast.error("Failed to load availability");
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    const bookedIds = new Set(bookings.map((b) => b.staff_id));
    const free = staff.filter((s) => !bookedIds.has(s.id));

    return (
        <div className="p-8 lg:p-10" data-testid="availability-page">
            <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest text-gray-400 mb-2">Resource planning</div>
                    <h1 className="font-serif text-4xl md:text-5xl text-gray-900">Staff Availability</h1>
                    <p className="text-sm text-gray-500 mt-2">
                        See who is booked and who is free on any given day.
                    </p>
                </div>
                <div>
                    <Label>Choose a date</Label>
                    <div className="flex items-center gap-2 mt-1">
                        <CalendarIcon className="w-4 h-4 text-gray-400" />
                        <Input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="w-48"
                            data-testid="availability-date-input"
                        />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-md">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-gray-400">Booked</div>
                            <h3 className="font-serif text-xl mt-0.5">On assignment</h3>
                        </div>
                        <div className="text-sm tabular-nums text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
                            {bookings.length}
                        </div>
                    </div>
                    {bookings.length === 0 ? (
                        <div className="p-6 text-sm text-gray-400">Everyone's free on this date.</div>
                    ) : (
                        <ul className="divide-y divide-gray-100" data-testid="booked-list">
                            {bookings.map((b, i) => (
                                <li key={i} className="px-6 py-3 flex items-center justify-between">
                                    <div>
                                        <div className="text-sm text-gray-900">{b.staff_name}</div>
                                        <div className="text-xs text-gray-500">
                                            {b.role} · {b.event_name} · {b.client_name}
                                        </div>
                                    </div>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-200">
                                        Booked
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="bg-white border border-gray-200 rounded-md">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-gray-400">Available</div>
                            <h3 className="font-serif text-xl mt-0.5">Free to book</h3>
                        </div>
                        <div className="text-sm tabular-nums text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">
                            {free.length}
                        </div>
                    </div>
                    {free.length === 0 ? (
                        <div className="p-6 text-sm text-gray-400">
                            <Users className="w-8 h-8 text-gray-300 mb-2" />
                            No one is free — add more staff members.
                        </div>
                    ) : (
                        <ul className="divide-y divide-gray-100" data-testid="free-list">
                            {free.map((s) => (
                                <li key={s.id} className="px-6 py-3 flex items-center justify-between">
                                    <div>
                                        <div className="text-sm text-gray-900">{s.name}</div>
                                        <div className="text-xs text-gray-500">{s.role}</div>
                                    </div>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                        Free
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}
